/**
 * Extracts the GraphQL operation name from a query/mutation/subscription document string — the
 * PascalCase name after the operation keyword, e.g. `GetPageLayout` from
 * `query GetPageLayout($id: String!) { ... }`.
 *
 * Single source of truth for operation-name derivation across the app: the API client uses it for
 * the `X-GraphQL-Operation-Name` request header, and the RUM latency instrumentation uses it as the
 * operation label (`meta.rumOp`).
 *
 * NOTE: This is the OPERATION name (`GetDecklines`), not the response FIELD name (`getDecklines`).
 * The GraphQL test mock (`test-utils/graphqlMocks.ts`) deliberately extracts the field name to route
 * responses — that is a different concept; do not consolidate the two.
 */

const OPERATION_NAME_PATTERN = /(?:query|mutation|subscription)\s+(\w+)/;

/**
 * @param queryDocument - the GraphQL document string (as generated in `queries.ts`/`mutations.ts`)
 * @param fallback - value returned when no operation name can be parsed (default `'unknownOperation'`)
 * @returns the operation name, or `fallback` if the document has none/anonymous
 */
export const extractGraphqlOperationName = (
  queryDocument: string | undefined,
  fallback = 'unknownOperation',
): string => {
  const match = queryDocument?.match(OPERATION_NAME_PATTERN);
  return match?.[1] ?? fallback;
};
